import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap, of } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { TicksheetService } from '@core/services/ticksheet.service';
import { TicksheetActions } from './ticksheet.actions';

@Injectable()
export class TicksheetEffects {
  private actions$ = inject(Actions);
  private ticksheetService = inject(TicksheetService);
  private translateService = inject(TranslateService);
  getTicksheetForms$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TicksheetActions.loadTicksheetForms),
      mergeMap((data) =>
        this.ticksheetService.getforms(data).pipe(
          map((ticksheets) =>
            ticksheets.success
              ? TicksheetActions.loadTicksheetFormsSuccess(ticksheets)
              : TicksheetActions.loadTicksheetFormsFailure(ticksheets)
          ),
          catchError((error) =>
            of(
              TicksheetActions.loadTicksheetFormsFailure({
                error,
                success: false,
                message: this.translateService.instant('Something went wrong'),
              })
            )
          )
        )
      )
    )
  );

  addFormData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TicksheetActions.addEditTicksheet),
      mergeMap((data) =>
        this.ticksheetService.addform(data).pipe(
          map((ticksheet) => {
            if (ticksheet.success) {
              TicksheetActions.loadTicksheetForms({});
              return TicksheetActions.addTicksheetSuccess(ticksheet);
            } else return TicksheetActions.addTicksheetFailure(ticksheet);
          }),
          catchError((error) =>
            of(
              TicksheetActions.addTicksheetFailure({
                error,
                success: false,
                message: this.translateService.instant('Something went wrong'),
              })
            )
          )
        )
      )
    )
  );

  createFormHeader$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TicksheetActions.createFormHeader),
      mergeMap((data) =>
        this.ticksheetService.createFormHeader(data).pipe(
          map((ticksheets) =>
            ticksheets.success
              ? TicksheetActions.createFormHeaderSuccess(ticksheets)
              : TicksheetActions.createFormHeaderFailure(ticksheets)
          ),
          catchError((error) =>
            of(
              TicksheetActions.createFormHeaderFailure({
                error,
                success: false,
                message: this.translateService.instant('Something went wrong'),
              })
            )
          )
        )
      )
    )
  );

  getFormDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TicksheetActions.loadFormDetails),
      mergeMap((data) =>
        this.ticksheetService.getFormDetails(data.id).pipe(
          map((ticksheets) =>
            ticksheets.success
              ? TicksheetActions.loadFormDetailsSuccess(ticksheets)
              : TicksheetActions.loadFormDetailsFailure(ticksheets)
          ),
          catchError((error) =>
            of(
              TicksheetActions.loadFormDetailsFailure({
                error,
                success: false,
                message: this.translateService.instant('Something went wrong'),
              })
            )
          )
        )
      )
    )
  );

  getTicksheets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TicksheetActions.loadTicksheets),
      mergeMap((data) =>
        this.ticksheetService.getTicksheets(data).pipe(
          map((ticksheets) =>
            ticksheets.success
              ? TicksheetActions.loadTicksheetsSuccess(ticksheets)
              : TicksheetActions.loadTicksheetsFailure(ticksheets)
          ),
          catchError((error) =>
            of(
              TicksheetActions.loadTicksheetsFailure({
                error,
                success: false,
                message: this.translateService.instant('Something went wrong'),
              })
            )
          )
        )
      )
    )
  );

  getTicksheetDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TicksheetActions.loadTicksheetDetails),
      mergeMap((data) =>
        this.ticksheetService.getTicksheetData(data.tick_sheet_id).pipe(
          map((ticksheets) =>
            ticksheets.success
              ? TicksheetActions.loadTicksheetDetailsSuccess(ticksheets)
              : TicksheetActions.loadTicksheetDetailsFailure(ticksheets)
          ),
          catchError((error) =>
            of(
              TicksheetActions.loadTicksheetDetailsFailure({
                error,
                success: false,
                message: this.translateService.instant('Something went wrong'),
              })
            )
          )
        )
      )
    )
  );

  getCheckedTicksheets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TicksheetActions.loadCheckedTicksheets),
      mergeMap((data) =>
        this.ticksheetService.getCheckedTicksheets(data).pipe(
          map((ticksheets) =>
            ticksheets.success
              ? TicksheetActions.loadCheckedTicksheetsSuccess(ticksheets)
              : TicksheetActions.loadCheckedTicksheetsFailure(ticksheets)
          ),
          catchError((error) =>
            of(
              TicksheetActions.loadCheckedTicksheetsFailure({
                error,
                success: false,
                message: this.translateService.instant('Something went wrong'),
              })
            )
          )
        )
      )
    )
  );

  updateRowTicksheet$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TicksheetActions.updateTicksheetRow),
      mergeMap((data) =>
        this.ticksheetService.updateTimesheetRow(data).pipe(
          map((ticksheets) =>
            ticksheets.success
              ? TicksheetActions.updateTicksheetRowSuccess(ticksheets)
              : TicksheetActions.updateTicksheetRowFailure(ticksheets)
          ),
          catchError((error) =>
            of(
              TicksheetActions.updateTicksheetRowFailure({
                error,
                success: false,
                message: this.translateService.instant('Something went wrong'),
              })
            )
          )
        )
      )
    )
  );

  updateTicksheetDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TicksheetActions.updateTicksheetDetails),
      mergeMap((data) =>
        this.ticksheetService.updateTimesheetDetails(data).pipe(
          map((ticksheets) =>
            ticksheets.success
              ? TicksheetActions.updateTicksheetDetailsSuccess(ticksheets)
              : TicksheetActions.updateTicksheetDetailsFailure(ticksheets)
          ),
          catchError((error) =>
            of(
              TicksheetActions.updateTicksheetDetailsFailure({
                error,
                success: false,
                message: this.translateService.instant('Something went wrong'),
              })
            )
          )
        )
      )
    )
  );
}
